import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../common/Button';
import ActionMenu from '../common/ActionMenu';
import {
    PhotoIcon,
    ClipboardDocumentIcon,
    TrashIcon,
    EyeIcon,
    MapPinIcon,
    ShareIcon
} from '@heroicons/react/24/outline';

// Helper to safely parse JSON or return the value if it's already an object/array
const safeJsonParse = (value) => {
    if (!value) return [];
    if (typeof value === 'object') return value; // Already processed
    try {
        return JSON.parse(value);
    } catch (e) {
        console.error('Failed to parse property_images:', e);
        return [];
    }
};

const ClientTourCard = ({
    tour,
    openImageViewer,
    copyLink,
    handleEditClick,
    handleStatusChange,
    handleDeleteTour,
    cities,
    propertyTypes
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const statusRef = useRef(null);

    // Close status menu on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setIsStatusOpen(false);
            }
        };

        if (isStatusOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isStatusOpen]);

    // Safe parse images once
    const propertyImages = safeJsonParse(tour.property_images);

    // Secondary Actions Menu Items
    const menuItems = [
        {
            label: t('copyLink'),
            icon: ClipboardDocumentIcon,
            onClick: () => copyLink(tour.id)
        },
        ...(propertyImages.length > 0 && (tour.file_path || tour.tour_url) ? [{
            label: t('viewImages'),
            icon: PhotoIcon,
            onClick: () => openImageViewer(propertyImages)
        }] : []),
        { type: 'divider' },
        {
            label: t('delete'),
            icon: TrashIcon,
            variant: 'danger',
            onClick: () => handleDeleteTour(tour.id)
        }
    ];

    // Status Options
    const statusOptions = [
        { label: t('statusDraft'), value: 'draft', color: 'gray' },
        { label: t('statusActive'), value: 'active', color: 'green' },
        { label: t('statusReserved'), value: 'reserved', color: 'amber' },
        { label: t('statusSold'), value: 'sold', color: 'red' }
    ];

    // Get color classes for current status
    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-success-500 text-white';
            case 'reserved': return 'bg-warning-500 text-white';
            case 'sold': return 'bg-danger-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    return (
        <div className="bg-surface-glass backdrop-blur-xl rounded-2xl shadow-card hover:shadow-glow-lg transition-all duration-300 border border-white/20 overflow-hidden group flex flex-col h-full">

            {/* Thumbnail Section */}
            <div className="relative aspect-[4/3] bg-surface-soft overflow-hidden shrink-0">
                {/* Sold Stamp */}
                {tour.status === 'sold' && (
                    <div className="absolute top-4 right-4 z-10 transform rotate-12 border-4 border-danger-600 text-danger-600 font-bold text-xl px-4 py-1 rounded opacity-90 bg-white/80 pointer-events-none">
                        {t('sold')}
                    </div>
                )}

                <a href={`/view-tour/${tour.id}`} target="_blank" rel="noreferrer" className="block w-full h-full relative group-hover:scale-110 transition-transform duration-700">
                    {tour.thumbnail_path ? (
                        <img
                            src={`${import.meta.env.VITE_API_URL || ''}/static/${tour.thumbnail_path}`}
                            alt={tour.title}
                            className={`w-full h-full object-cover ${tour.status === 'sold' ? 'grayscale' : ''}`}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-text-muted bg-gray-100">
                            <span className="text-4xl opacity-20">No Image</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                </a>

                {/* Status Badge (Dropdown Trigger) */}
                <div className="absolute top-3 left-3 z-20 pointer-events-auto" ref={statusRef}>
                    <button
                        onClick={() => setIsStatusOpen(!isStatusOpen)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg flex items-center gap-1 transition-transform active:scale-95 ${getStatusColor(tour.status)}`}
                        title={t('clickToChangeStatus')}
                    >
                        {t(`status${tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}`)}
                        <span className={`transition-transform duration-200 ${isStatusOpen ? 'rotate-180' : ''}`}>▾</span>
                    </button>

                    {/* Status Dropdown */}
                    {isStatusOpen && (
                        <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden animate-slide-up z-50">
                            {statusOptions.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => {
                                        handleStatusChange(tour.id, opt.value);
                                        setIsStatusOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gold/5 flex items-center gap-2 transition-colors ${tour.status === opt.value ? 'bg-gold/10 font-medium text-gold-dark' : 'text-gray-700'
                                        }`}
                                >
                                    <span className={`w-2 h-2 rounded-full ${opt.color === 'green' ? 'bg-green-500' :
                                            opt.color === 'amber' ? 'bg-amber-500' :
                                                opt.color === 'red' ? 'bg-red-500' : 'bg-gray-400'
                                        }`} />
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Price Badge */}
                {tour.show_price && tour.price && (
                    <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-bg-card/95 backdrop-blur-sm rounded-lg text-sm font-bold text-gold shadow-lg border border-gold/20 pointer-events-none">
                        {tour.price.toLocaleString()} {tour.currency ? t(`currency${tour.currency}`) : ''}
                    </div>
                )}

                {/* Info Overlay (Views) */}
                {tour.view_count > 0 && (
                    <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-md text-xs text-white border border-white/10 flex items-center gap-1 pointer-events-none">
                        <EyeIcon className="w-3 h-3 text-white/80" />
                        {tour.view_count}
                    </div>
                )}

                {/* Action Menu (Top Right) */}
                <div className="absolute top-3 right-3 z-20 pointer-events-auto bg-bg-card/90 hover:bg-bg-card backdrop-blur-sm rounded-full text-gold hover:text-white transition-all shadow-lg min-w-[44px] min-h-[44px] flex items-center justify-center border border-gold/20">
                    <ActionMenu items={menuItems} />
                </div>
            </div>

            {/* Content Section */}
            <div className="p-5 flex flex-col flex-grow">
                <h3 className="font-bold text-text text-lg mb-3 line-clamp-1" title={tour.title}>
                    {tour.title}
                </h3>

                <div className="space-y-3 text-sm text-text-muted mb-4 flex-grow">
                    {/* Location */}
                    <div className="flex items-center gap-2">
                        <MapPinIcon className="h-4 w-4 flex-shrink-0 text-text-muted" />
                        <span className="line-clamp-1">
                            {(() => {
                                const cityObj = cities?.find(c => c.en.toLowerCase() === tour.city?.toLowerCase());
                                const subareaObj = cityObj?.subareas?.find(s => s.en.toLowerCase() === tour.area?.toLowerCase());
                                const translatedCity = i18n.language === 'ar' ? (cityObj?.ar || tour.city) : (cityObj?.en || tour.city);
                                const translatedArea = tour.area 
                                    ? (i18n.language === 'ar' ? (subareaObj?.ar || tour.area) : (subareaObj?.en || tour.area)) 
                                    : '';
                                return translatedArea 
                                    ? (i18n.language === 'ar' ? `${translatedCity}، ${translatedArea}` : `${translatedCity}, ${translatedArea}`) 
                                    : translatedCity;
                            })()}
                        </span>
                    </div>

                    {/* Meta Details */}
                    <div className="flex items-center justify-between pt-3 border-t border-gold/10">
                        <div className="flex gap-4 text-text-muted">
                            {tour.rooms && <span className="flex items-center gap-1"><span className="font-semibold">{tour.rooms}</span> {t('beds')}</span>}
                            {tour.size && <span className="flex items-center gap-1"><span className="font-semibold">{tour.size}</span> m²</span>}
                        </div>
                        {tour.property_type && (
                            <div className="text-gold font-medium text-xs bg-gold/10 px-2 py-1 rounded border border-gold/20">
                                {propertyTypes && propertyTypes.length > 0
                                    ? (i18n.language === 'ar'
                                        ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase())?.ar || tour.property_type
                                        : propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase())?.en || tour.property_type)
                                    : tour.property_type}
                            </div>
                        )}
                    </div>
                    {tour.property_id && (
                        <div className="text-xs text-text-muted/60 mt-2">ID: {tour.property_id}</div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3 mt-auto">
                    <Button
                        onClick={() => handleEditClick(tour)}
                        variant="secondary"
                        className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200 py-3 rounded-xl min-h-[44px]"
                    >
                        {t('edit')}
                    </Button>

                    {(tour.file_path || tour.tour_url) ? (
                        <Button
                            href={`/view-tour/${tour.id}`}
                            target="_blank"
                            rel="noreferrer"
                            variant="primary"
                            className="bg-gold-gradient text-white border-none py-3 rounded-xl shadow-glow hover:shadow-glow-lg min-h-[44px]"
                        >
                            {t('view3DTour')}
                        </Button>
                    ) : propertyImages.length > 0 ? (
                        <Button
                            onClick={() => openImageViewer(propertyImages)}
                            variant="primary"
                            className="bg-gold-gradient text-white border-none py-3 rounded-xl shadow-glow hover:shadow-glow-lg min-h-[44px]"
                        >
                            {t('viewImages')}
                        </Button>
                    ) : (
                        <Button disabled className="opacity-50 cursor-not-allowed py-3 rounded-xl">
                            {t('processing')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientTourCard;
